import { default as React } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import {
  Entity,
  parseEntityRef,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  RELATION_HAS_PART,
} from '@backstage/catalog-model';
import {
  entityRouteRef,
  entityRouteParams,
} from '@backstage/plugin-catalog-react';
import { useRouteRef } from '@backstage/core-plugin-api';
import { Link } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.2s ease-in-out',
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    flex: 1,
  },
  name: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  externalIcon: {
    padding: 4,
    color: theme.palette.text.secondary,
    opacity: 0.5,
  },
  description: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: 1.5,
    marginBottom: theme.spacing(1.5),
  },
  badges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
    alignItems: 'center',
  },
  badgeLabel: {
    fontWeight: 600,
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    flexShrink: 0,
    lineHeight: 1,
  },
  typeChip: {
    height: 18,
    fontSize: '0.62rem',
    fontWeight: 500,
    borderColor: theme.palette.text.secondary,
  },
  lifecycleChip: {
    height: 20,
    fontSize: '0.7rem',
    fontWeight: 600,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  lifecycleStaging: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  lifecycleDev: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
  },
  tagChip: {
    height: 18,
    fontSize: '0.65rem',
    fontWeight: 400,
    borderColor: theme.palette.divider,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
    marginBottom: theme.spacing(1),
    fontSize: '0.825rem',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    overflow: 'hidden',
  },
  metaLabel: {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  metaValue: {
    fontSize: '0.825rem',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaLink: {
    fontSize: '0.825rem',
    color: theme.palette.primary.main,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center',
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 22,
    height: 20,
    padding: '0 6px',
    borderRadius: 10,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    fontSize: '0.72rem',
    fontWeight: 600,
    lineHeight: 1,
  },
  footer: {
    marginTop: 'auto',
  },
  statusIcon: {
    fontSize: '1.2rem',
    flexShrink: 0,
  },
}));

function getLifecycleChipClass(
  lifecycle: string | undefined,
  classes: ReturnType<typeof useStyles>,
): string {
  switch (lifecycle) {
    case 'production':
      return classes.lifecycleChip;
    case 'staging':
      return `${classes.lifecycleChip} ${classes.lifecycleStaging}`;
    case 'experimental':
    case 'development':
      return `${classes.lifecycleChip} ${classes.lifecycleDev}`;
    default:
      return classes.lifecycleChip;
  }
}

export function LifecycleStatusIcon({
  lifecycle,
  className,
}: {
  lifecycle?: string;
  className?: string;
}) {
  const theme = useTheme();
  switch (lifecycle) {
    case 'production':
      return (
        <CheckCircleIcon
          className={className}
          style={{ color: theme.palette.success.main }}
        />
      );
    case 'experimental':
    case 'development':
      return (
        <ScheduleIcon
          className={className}
          style={{ color: theme.palette.info.main }}
        />
      );
    case 'staging':
      return (
        <ScheduleIcon
          className={className}
          style={{ color: theme.palette.warning.main }}
        />
      );
    default:
      return null;
  }
}

export function isUnknownOwner(ref: string): boolean {
  return ref.endsWith('/unknown');
}

export function getOwnerName(entity: Entity): string {
  const ownerRelation = entity.relations?.find(
    r => r.type === RELATION_OWNED_BY,
  );
  if (!ownerRelation) return '-';
  const ref = ownerRelation.targetRef;
  if (isUnknownOwner(ref)) return '-';
  const name = ref.split('/').pop() || ref;
  return name;
}

function getOwnerTargetRef(entity: Entity): string | null {
  const ownerRelation = entity.relations?.find(
    r => r.type === RELATION_OWNED_BY,
  );
  const targetRef = ownerRelation?.targetRef || null;
  if (targetRef && isUnknownOwner(targetRef)) return null;
  return targetRef;
}

function getSystemTargetRef(entity: Entity): string | null {
  const systemRelation = entity.relations?.find(
    r => r.type === RELATION_PART_OF && r.targetRef.startsWith('system:'),
  );
  return systemRelation?.targetRef || null;
}

function getDomainTargetRef(entity: Entity): string | null {
  const domainRelation = entity.relations?.find(
    r => r.type === RELATION_PART_OF && r.targetRef.startsWith('domain:'),
  );
  return domainRelation?.targetRef || null;
}

const GENERAL_MANAGER_ANNOTATION = 'skystage.wellsky.com/general-manager';
const LEGACY_NAME_ANNOTATION = 'skystage.wellsky.com/legacy-name';

function getGeneralManagerRef(entity: Entity): string | null {
  const annotation =
    entity.metadata?.annotations?.[GENERAL_MANAGER_ANNOTATION] || '';
  if (!annotation) return null;
  const isEntityRef = annotation.includes(':');
  return isEntityRef ? annotation : `user:onspring/${annotation}`;
}

function getDisplayName(targetRef: string): string {
  return targetRef.split('/').pop() || targetRef;
}

export const CatalogEntityCard = ({
  entity,
  onClick,
}: {
  entity: Entity;
  onClick?: (entity: Entity) => void;
}) => {
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);

  const name = entity.metadata.title || entity.metadata.name;
  const description = entity.metadata.description || '';
  const type = (entity.spec as Record<string, unknown>)?.type as
    | string
    | undefined;
  const lifecycle = (entity.spec as Record<string, unknown>)?.lifecycle as
    | string
    | undefined;
  const kind = entity.kind?.toLowerCase() || 'component';
  const ownerTargetRef = getOwnerTargetRef(entity);
  const systemTargetRef = getSystemTargetRef(entity);
  const domainTargetRef = getDomainTargetRef(entity);
  const generalManagerRef =
    kind === 'system' ? getGeneralManagerRef(entity) : null;
  const legacyName =
    kind === 'system'
      ? entity.metadata?.annotations?.[LEGACY_NAME_ANNOTATION] || ''
      : '';
  const tags = entity.metadata.tags || [];

  const entityLink = entityRoute(entityRouteParams(entity));
  const ownerLink = ownerTargetRef
    ? entityRoute(entityRouteParams(parseEntityRef(ownerTargetRef)))
    : null;
  const systemLink = systemTargetRef
    ? entityRoute(entityRouteParams(parseEntityRef(systemTargetRef)))
    : null;
  const domainLink = domainTargetRef
    ? entityRoute(entityRouteParams(parseEntityRef(domainTargetRef)))
    : null;
  const gmLink = generalManagerRef
    ? entityRoute(entityRouteParams(parseEntityRef(generalManagerRef)))
    : null;

  const handleCardClick = () => {
    if (onClick) {
      onClick(entity);
    }
  };

  const handleExternalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(entityLink, '_blank');
  };

  return (
    <Card className={classes.card} variant="outlined" onClick={handleCardClick}>
      <CardContent className={classes.cardContent}>
        {/* Row 1: Name + status icon + external link */}
        <div className={classes.header}>
          <div className={classes.nameRow}>
            <Typography className={classes.name}>{name}</Typography>
            <LifecycleStatusIcon
              lifecycle={lifecycle}
              className={classes.statusIcon}
            />
          </div>
          <IconButton
            className={classes.externalIcon}
            size="small"
            onClick={handleExternalClick}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Row 2: Description */}
        {description && (
          <Typography className={classes.description}>{description}</Typography>
        )}

        {/* Footer section: badges + meta + tags grouped together */}
        <div className={classes.footer}>
          {/* Row 3: Kind/Type + Lifecycle badges */}
          <div className={classes.badges}>
            <span className={classes.badgeLabel}>Type:</span>
            <Chip
              label={type || entity.kind}
              size="small"
              variant="outlined"
              className={classes.typeChip}
            />
            {lifecycle && (
              <>
                <span className={classes.badgeLabel}>Lifecycle:</span>
                <Chip
                  label={lifecycle}
                  size="small"
                  className={getLifecycleChipClass(lifecycle, classes)}
                />
              </>
            )}
          </div>

          {/* Row 4: Metadata grid - adapts per entity kind */}
          <div className={classes.metaGrid}>
            {/* Owner - shown for all kinds */}
            <div className={classes.metaItem}>
              <span className={classes.metaLabel}>Owner:</span>
              {ownerLink && ownerTargetRef ? (
                <Link
                  to={ownerLink}
                  className={classes.metaLink}
                  onClick={e => e.stopPropagation()}
                >
                  {getDisplayName(ownerTargetRef)}
                </Link>
              ) : (
                <span className={classes.metaValue} />
              )}
            </div>

            {/* System - shown for Component kind */}
            {kind === 'component' && (
              <div className={classes.metaItem}>
                <span className={classes.metaLabel}>System:</span>
                {systemLink && systemTargetRef ? (
                  <Link
                    to={systemLink}
                    className={classes.metaLink}
                    onClick={e => e.stopPropagation()}
                  >
                    {getDisplayName(systemTargetRef)}
                  </Link>
                ) : (
                  <span className={classes.metaValue}>-</span>
                )}
              </div>
            )}

            {/* Domain - shown for System kind */}
            {kind === 'system' && (
              <div className={classes.metaItem}>
                <span className={classes.metaLabel}>Domain:</span>
                {domainLink && domainTargetRef ? (
                  <Link
                    to={domainLink}
                    className={classes.metaLink}
                    onClick={e => e.stopPropagation()}
                  >
                    {getDisplayName(domainTargetRef)}
                  </Link>
                ) : (
                  <span className={classes.metaValue}>-</span>
                )}
              </div>
            )}

            {/* General Manager - shown for System kind */}
            {kind === 'system' && (
              <div className={classes.metaItem}>
                <span className={classes.metaLabel}>GM:</span>
                {gmLink && generalManagerRef ? (
                  <Link
                    to={gmLink}
                    className={classes.metaLink}
                    onClick={e => e.stopPropagation()}
                  >
                    {getDisplayName(generalManagerRef)}
                  </Link>
                ) : (
                  <span className={classes.metaValue}>-</span>
                )}
              </div>
            )}

            {/* Legacy Name - shown for System kind */}
            {kind === 'system' && legacyName && (
              <div className={classes.metaItem}>
                <span className={classes.metaLabel}>Legacy:</span>
                <span className={classes.metaValue}>{legacyName}</span>
              </div>
            )}

            {/* Systems count - shown for Domain kind */}
            {kind === 'domain' && (
              <div className={classes.metaItem}>
                <span className={classes.metaLabel}>Systems:</span>
                <span className={classes.countBadge}>
                  {entity.relations?.filter(r => r.type === RELATION_HAS_PART)
                    .length || 0}
                </span>
              </div>
            )}
          </div>

          {/* Row 5: Tags */}
          {tags.length > 0 && (
            <div className={classes.tagsRow}>
              <span className={classes.badgeLabel}>Tags:</span>
              {tags.slice(0, 4).map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  className={classes.tagChip}
                />
              ))}
              {tags.length > 4 && (
                <Chip
                  label={`+${tags.length - 4}`}
                  size="small"
                  variant="outlined"
                  className={classes.tagChip}
                />
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
